import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { Seo } from '@/components/generals/seo'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { SiteFooter } from '@/components/docs/docs-layout'
import { SiteHeader } from '@/components/docs/site-header'
import { ScaledFrame } from '@/components/docs/block-frame'
import { COMPONENT_NAV } from '@/constants/docs-nav'

import {
    ArrowRightIcon,
    BrowsersIcon,
    ChartLineUpIcon,
    CubeIcon,
    PaletteIcon,
    SettingsIcon,
    SquaresFourIcon
} from '@/components/icons/icon-set'

const EXAMPLES = [
    { id: 'dashboard', label: 'Dashboard', view: 'dashboard-overview', height: 720 },
    { id: 'inbox', label: 'Inbox', view: 'kanban-template-inbox', height: 720 },
    { id: 'drawer', label: 'Drawer', view: 'drawer-revenue-people', height: 720 },
    { id: 'tables', label: 'Tables', view: 'table-supporters', height: 620 }
]

const SECTIONS = [
    { title: 'Foundations', description: 'Semantic colors in light and dark, the raw palette, radius, effects and the twelve text styles.', href: '/docs/foundations/semantic-colors', icon: PaletteIcon },
    { title: 'Components', description: `${COMPONENT_NAV.length} components with every variant, size and state from the Figma file.`, href: '/docs/components', icon: CubeIcon },
    { title: 'Charts', description: 'The shared chart anatomy and nine approved series, with area, line, bar and stacked plots.', href: '/docs/charts', icon: ChartLineUpIcon },
    { title: 'Blocks', description: 'App layout, chart cards, drawer content, kanban and tables, ready to compose.', href: '/docs/blocks/app-layout', icon: SquaresFourIcon },
    { title: 'Templates', description: 'Page layouts, drawers and kanban boards, in light and dark.', href: '/docs/templates/page-layouts', icon: BrowsersIcon },
    { title: 'Settings', description: 'The project guide every build follows, and your own rules for writing, drawers and layout.', href: '/settings', icon: SettingsIcon }
]

/* Home: shadcn's home layout with GoodUnited's own styling, featuring the dashboard. */
const Home: FC = (): ReactNode => {
    return (
        <div className='flex min-h-dvh flex-col bg-surface-page text-text-primary'>
            <Seo />
            <SiteHeader />

            <main id='main' className='mx-auto flex w-full max-w-384 flex-1 flex-col gap-16 px-4 pt-16 pb-20 sm:px-6 lg:px-8 lg:pt-24'>
                <section id='hero' className='mx-auto flex max-w-3xl flex-col items-center gap-6 text-center'>
                    <Link href='/docs/templates/page-layouts' className='inline-flex items-center gap-2 rounded-full border border-border-subtle bg-surface-raised py-1 pr-3 pl-1 text-sm text-text-primary transition-colors outline-none hover:bg-surface-subtle focus-visible:outline-2 focus-visible:outline-border-focus'>
                        <Badge variant='success' shape='pill'>New</Badge>
                        Templates for page layouts, drawers and kanban
                        <ArrowRightIcon className='size-4 text-icon-secondary' aria-hidden='true' />
                    </Link>
                    <h1 className='font-heading text-4xl font-semibold text-text-primary'>
                        The GoodUnited design system
                    </h1>
                    <p className='max-w-2xl text-base text-text-secondary'>
                        Every color, type style, component, block and template from the GoodUnited Figma file, built and working. Use it to design and build any GoodUnited screen the same way, in light and dark.
                    </p>
                    <div className='flex flex-wrap justify-center gap-3'>
                        <Button size='lg' render={<Link href='/docs' />} nativeButton={false}>
                            Get started
                            <ArrowRightIcon aria-hidden='true' />
                        </Button>
                        <Button size='lg' variant='outline' render={<Link href='/docs/blocks/app-layout' />} nativeButton={false}>
                            Browse blocks
                        </Button>
                    </div>
                </section>

                <section id='examples' aria-label='Examples' className='flex flex-col gap-4'>
                    <Tabs defaultValue='dashboard' className='flex flex-col gap-4'>
                        <div className='flex flex-wrap items-center justify-between gap-3'>
                            <TabsList>
                                {EXAMPLES.map(example => (
                                    <TabsTrigger key={example.id} value={example.id}>
                                        {example.label}
                                    </TabsTrigger>
                                ))}
                            </TabsList>
                            <Button variant='link' render={<Link href='/docs/templates/page-layouts' />} nativeButton={false}>
                                All templates
                                <ArrowRightIcon aria-hidden='true' />
                            </Button>
                        </div>
                        {EXAMPLES.map(example => (
                            <TabsContent key={example.id} value={example.id}>
                                <ScaledFrame id={example.view} title={example.label} height={example.height} width={1280} />
                            </TabsContent>
                        ))}
                    </Tabs>
                </section>

                <section id='whats-inside' aria-labelledby='whats-inside-title' className='flex flex-col gap-6'>
                    <h2 id='whats-inside-title' className='font-heading text-2xl font-semibold text-text-primary'>
                        What is inside
                    </h2>
                    <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3'>
                        {SECTIONS.map(section => (
                            <Link
                                key={section.title}
                                href={section.href}
                                className='group flex flex-col gap-3 rounded-xl border border-border-subtle bg-surface-raised p-6 transition-colors outline-none hover:border-border-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus'
                            >
                                <span className='inline-flex size-10 items-center justify-center rounded-lg bg-surface-muted text-icon-primary'>
                                    <section.icon className='size-5' aria-hidden='true' />
                                </span>
                                <span className='flex items-center justify-between gap-2 font-heading text-lg font-semibold text-text-primary'>
                                    {section.title}
                                    <ArrowRightIcon className='size-4 text-icon-tertiary transition-transform group-hover:translate-x-0.5' aria-hidden='true' />
                                </span>
                                <span className='text-base text-text-secondary'>
                                    {section.description}
                                </span>
                            </Link>
                        ))}
                    </div>
                </section>
            </main>

            <SiteFooter />
        </div>
    )
}

export default Home
